/**
 * Trusted-device token storage for the OTP login flow.
 *
 * When a user ticks "trust this device" during OTP verification the backend
 * returns an opaque `device_token`. Sending that token on the next login lets
 * the backend skip the OTP step until the token's hard expiry.
 *
 * Tokens are kept per email address so that several people sharing one browser
 * each keep their own trust, and are stored outside redux-persist because they
 * must survive a logout (which resets the persisted user slice).
 */

const STORAGE_KEY = "salarypay_trusted_devices";

type TrustedDeviceMap = Record<string, string>;

const normalizeEmail = (email: string) => (email || "").trim().toLowerCase();

const readAll = (): TrustedDeviceMap => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const writeAll = (map: TrustedDeviceMap) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (e) {
    // storage unavailable (private mode / quota) - trust simply won't persist
  }
};

/** Returns the stored device token for an email, or "" when the device isn't trusted. */
const getDeviceToken = (email: string): string => {
  const key = normalizeEmail(email);
  if (!key) return "";
  return readAll()[key] || "";
};

/** Stores the backend-issued device token against an email. */
const setDeviceToken = (email: string, token: string) => {
  const key = normalizeEmail(email);
  if (!key || !token) return;
  const map = readAll();
  map[key] = token;
  writeAll(map);
};

/** Forgets the device token for an email (e.g. after the backend rejects it). */
const clearDeviceToken = (email: string) => {
  const key = normalizeEmail(email);
  if (!key) return;
  const map = readAll();
  if (map[key]) {
    delete map[key];
    writeAll(map);
  }
};

/** A human readable label for this browser, shown in the trusted-device list. */
const getDeviceName = (): string => {
  const ua = navigator.userAgent || "";

  const browser =
    /Edg\//.test(ua)
      ? "Edge"
      : /OPR\//.test(ua)
      ? "Opera"
      : /Chrome\//.test(ua)
      ? "Chrome"
      : /Safari\//.test(ua)
      ? "Safari"
      : /Firefox\//.test(ua)
      ? "Firefox"
      : "Browser";

  const platform = /Windows/.test(ua)
    ? "Windows"
    : /Android/.test(ua)
    ? "Android"
    : /iPhone|iPad|iPod/.test(ua)
    ? "iOS"
    : /Mac OS X/.test(ua)
    ? "macOS"
    : /Linux/.test(ua)
    ? "Linux"
    : "Unknown OS";

  return `${browser} on ${platform}`;
};

export const TrustedDevice = {
  getDeviceToken,
  setDeviceToken,
  clearDeviceToken,
  getDeviceName,
};
